import { ButtonWithLoading, ErrorModal, MesfModal } from "@dexteel/mesf-core";
import { Checkbox, FormControlLabel } from "@mui/material";
import Button from "@mui/material/Button";
import Grid2 from "@mui/material/Grid2";
import Snackbar from "@mui/material/Snackbar";
import Alert from "@mui/lab/Alert";
import { type SyntheticEvent, useEffect, useState } from "react";
import { Controller, useForm } from "react-hook-form";
import { useFormsContext } from "@setup/configurations/forms/context/FormsContext";
import type { UserFormAssetData } from "@setup/configurations/forms/models/userFormAssetData";
import { INITIAL_VALUES_USER_FORM_ASSET } from "@setup/configurations/forms/models/UserFormAssetInitialValue";
import { upsertUserFormAsset } from "@setup/configurations/forms/repositories/FormsRepository";
import { UsersPicker } from "../picker/usersPicker";

type Props = {
	show: boolean;
	onHide: (shouldUpdate: boolean) => void;
	setShouldUpdateWhenExit: (shouldUpdate: boolean) => void;
	data: UserFormAssetData | null;
	newMode?: boolean;
	FormAssetIdToSearchUserFormAssets: number;
};

export const NewAndEditUserFormAssetModal = ({
	show,
	onHide,
	data,
	newMode = false,
	setShouldUpdateWhenExit,
	FormAssetIdToSearchUserFormAssets,
}: Props) => {
	const {
		state: {},
		actions: {
			setUserIdFormAssetDataSelectedInTable,
			setIsDefaultUserFormAssetDataSelectedInTable,
			setFormAssetId,
		},
	} = useFormsContext();

	const [editedSuccess, setEditedSuccess] = useState(false);
	const [isSubmitLoading, setIsSubmitLoading] = useState(false);

	const [error, setError] = useState<string>("");
	const [deleteByIdError, setDeleteByIdError] = useState<string>("");
	const { control, handleSubmit, watch, setValue, reset } =
		useForm<UserFormAssetData>({
			defaultValues: INITIAL_VALUES_USER_FORM_ASSET,
		});

	//states

	const handleClose = (
		event: Event | React.SyntheticEvent,
		reason?: string,
	) => {
		if (reason === "clickaway") {
			return;
		}
		setEditedSuccess(false);
	};
	const handleCancel = async () => {
		reset();
		onHide(false);
	};

	const userId = watch("UserId");
	const changeUserId = () => {
		setUserIdFormAssetDataSelectedInTable(userId);
	};

	const isDefault = watch("IsDefault");
	const changeIsDefault = () => {
		setIsDefaultUserFormAssetDataSelectedInTable(isDefault);
	};

	const onSubmit = async (UserFormAssetData: UserFormAssetData) => {
		setIsSubmitLoading(true);
		const res = await upsertUserFormAsset(
			UserFormAssetData.UserId,
			FormAssetIdToSearchUserFormAssets,
			UserFormAssetData.IsDefault,
		);
		if (res.ok) {
			reset();
			setFormAssetId(null);
			setIsSubmitLoading(false);
			setEditedSuccess(true);
			setShouldUpdateWhenExit(true);
			onHide(true);
		} else {
			setIsSubmitLoading(false);
			setError(res.message);
		}
	};

	useEffect(() => {
		if (data != null && show) {
			reset(INITIAL_VALUES_USER_FORM_ASSET);
			setValue("UserId", data.UserId);
			setValue("IsDefault", data.IsDefault);
			setIsSubmitLoading(false);
		} else {
			reset(INITIAL_VALUES_USER_FORM_ASSET);
		}
	}, [show]);

	useEffect(() => {
		changeUserId();
	}, [userId]);

	useEffect(() => {
		changeIsDefault();
	}, [isDefault]);

	return (
		<>
			<MesfModal
				title={newMode ? "NEW USER" : "EDIT USER"}
				open={show}
				handleClose={() => handleCancel()}
				maxWidth="sm"
				id="user-form-asset-modal"
			>
				<form onSubmit={handleSubmit(onSubmit)}>
					<MesfModal.Content dividers>
						<Grid2 container spacing={2} alignItems="center">
							<Grid2 size={{ md: 7, xs: 12 }}>
								<Controller
									name="UserId"
									control={control}
									rules={{ required: "User is required" }}
									render={({ field: { onChange, value } }) => (
										<UsersPicker
											value={value as number}
											onChange={onChange}
											disabled={!newMode}
										/>
									)}
								/>
							</Grid2>
							<Grid2 size={{ md: 5, xs: 12 }}>
								<Controller
									name="IsDefault"
									control={control}
									render={({ field }) => (
										<FormControlLabel
											sx={{ userSelect: "none" }}
											control={
												<Checkbox
													checked={field.value as boolean}
													onChange={field.onChange}
													name="IsDefault"
													color="primary"
												/>
											}
											label="Default for User"
										/>
									)}
								/>
							</Grid2>
						</Grid2>
					</MesfModal.Content>
					<MesfModal.Actions style={{ padding: "20px 16px 16px" }}>
						<Grid2 container spacing={2} justifyContent="flex-end" width="100%">
							<Grid2 size={{ md: 3, xs: 12 }}>
								<Button
									fullWidth
									variant="contained"
									color="inherit"
									onClick={handleCancel}
								>
									Cancel
								</Button>
							</Grid2>
							<Grid2 size={{ md: 3, xs: 12 }}>
								<ButtonWithLoading
									fullWidth
									isLoading={isSubmitLoading}
									variant="contained"
									color="primary"
									type="submit"
								>
									Save
								</ButtonWithLoading>
							</Grid2>
						</Grid2>
					</MesfModal.Actions>
				</form>
			</MesfModal>
			<Snackbar
				open={editedSuccess}
				autoHideDuration={2500}
				onClose={handleClose}
				anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
			>
				<Alert severity="info" onClose={handleClose}>
					Success
				</Alert>
			</Snackbar>
			<ErrorModal error={error} onHide={() => setError("")} />
			<ErrorModal
				error={deleteByIdError}
				onHide={() => setDeleteByIdError("")}
			/>
		</>
	);
};
